import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';

/**
 * Единая локализация Angular Material paginator.
 *
 * Material paginator не читает ngx-translate сам, поэтому без этого сервиса
 * подписи вроде `Items per page` и `1-100 of 7055` остаются английскими даже
 * на русской странице. Сервис обновляет подписи при смене языка и уведомляет
 * уже отрисованные paginator через `changes`.
 */
@Injectable()
export class AppPaginatorIntl extends MatPaginatorIntl {
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.updateLabels();

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateLabels());

    this.translate.onTranslationChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateLabels());
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const total = Math.max(0, length);

    if (total === 0 || pageSize === 0) {
      return this.translate.instant('common.paginator.range-empty', { total });
    }

    const startIndex = page * pageSize;
    const from = startIndex + 1;
    const to = Math.min(startIndex + pageSize, total);

    return this.translate.instant('common.paginator.range', { from, to, total });
  };

  private updateLabels(): void {
    this.itemsPerPageLabel = this.translate.instant('common.paginator.items-per-page');
    this.nextPageLabel = this.translate.instant('common.paginator.next-page');
    this.previousPageLabel = this.translate.instant('common.paginator.previous-page');
    this.firstPageLabel = this.translate.instant('common.paginator.first-page');
    this.lastPageLabel = this.translate.instant('common.paginator.last-page');
    this.changes.next();
  }
}
